import { Box } from "flicket-ui";
import { formatPrice } from "@flicket/utils";
import { useContext, useEffect } from "react";
import { BaseContext } from "~context";
import {
  BNPLIntegrationType,
  CustomPaymentType,
  isBNPLIntegrationType,
  PaymentOption,
} from "~features/checkout/common/checkout.types";
import {
  AFTERPAY_CONFIG,
  BNPL_CONFIG,
  PARTIAL_POINT_PAYMENT_TYPES,
} from "~features/checkout/common/constants";
import { GetTotalsReturn } from "~features/checkout/legacy/helpers/getTotals";
import { IntegrationType } from "~graphql/sdk";
import { useOrganization } from "~hooks";
import { calculateBNPLPrice } from "~lib";
import { HoldOrderModal } from "./checkout.HoldOrder.modal";
import { withVariantCheckoutBNPLModal } from "./checkout.BNPL.modal";
import PaymentItem from "./checkout.Payment.listItem";
import { PaymentPlansModal } from "./checkout.PaymentPlans.modal";

export interface PaymentListProps {
  paymentType: "full" | "partial";
  paymentOptions: PaymentOption[];
  totals: GetTotalsReturn;
  selectedId: string;
  onClick: (paymentValue: string, partPaymentValue?: string) => void;
  showHiddenFees: boolean;
  totalForBNPL: number;
  eventId?: string;
  membershipId?: string;
}

export type ViewModelProps = PaymentListProps & {
  pinPaymentEnabled?: boolean;
};

export type PaymentOptionVM = {
  title: string;
  subtitle: string;
  image: string | null;
  icon: string | null;
  type: PaymentOption["type"];
  value: string;
  isActive: boolean;
  imageMaxWidth?: string;
  paymentPlanMetadata?: PaymentOption["paymentPlanMetadata"];
  eventId?: string;
  membershipId?: string;
  order: number;
};

export const usePaymentListViewModel = ({
  paymentType,
  paymentOptions = [],
  selectedId,
  onClick,
  showHiddenFees,
  pinPaymentEnabled,
  totalForBNPL: totalForLaybuy,
  eventId,
  membershipId,
}: ViewModelProps) => {
  const { organization } = useContext(BaseContext);

  const tempPaymentOptionsVM = paymentOptions
    .filter(ifPartialPayment)
    .filter(removePinIfFeatureFlagNotEnabled)
    .filter(removeBNPLIfUnderThreshold)
    .filter(removeBNPLIfOverThreshold)
    .map(configureOptions);

  const paymentOptionsVM = [...tempPaymentOptionsVM];

  // Hack: SYNTHONY
  // Remove 2025-04-01
  if (eventId === "d1d7d83f-0f53-461b-835d-21347c852775") {
    const typeOrder = {
      PAYMENT_PIN: 1,
      PAYMENT_STRIPE: 2,
      PAYMENT_LAYBUY: 3,
    };
    paymentOptionsVM.sort(
      (a, b) => (typeOrder[a.type] || 99) - (typeOrder[b.type] || 99)
    );
  }

  // Default payment option if none selected
  useEffect(() => {
    if (paymentOptionsVM?.length > 0 && !selectedId) {
      const defaultOption = paymentOptionsVM[0];
      onClick(defaultOption.value);
    }
  }, [paymentOptionsVM]);

  const handleItemClick = (paymentValue: string, partPaymentValue: string) => {
    onClick(paymentValue, partPaymentValue);
  };

  function removePinIfFeatureFlagNotEnabled({ type }) {
    if (type !== IntegrationType.PaymentPin) {
      return true;
    }
    return pinPaymentEnabled;
  }

  function removeBNPLIfUnderThreshold({ type, bnplEnabled }: PaymentOption) {
    return !(
      (type === IntegrationType.PaymentLaybuy ||
        (type === IntegrationType.PaymentStripe && bnplEnabled)) &&
      totalForLaybuy < 6
    );
  }

  function removeBNPLIfOverThreshold({ type, bnplEnabled }: PaymentOption) {
    return !(
      (type === IntegrationType.PaymentLaybuy ||
        (type === IntegrationType.PaymentStripe && bnplEnabled)) &&
      totalForLaybuy > 2000
    );
  }

  function ifPartialPayment(option) {
    return paymentType === "partial"
      ? PARTIAL_POINT_PAYMENT_TYPES.includes(option.type as IntegrationType)
      : true;
  }

  function configureOptions(item) {
    const {
      id,
      description,
      value,
      label,
      type,
      paymentMethodType,
      paymentPlanMetadata,
    } = item;

    const option: PaymentOptionVM = {
      title: label as string,
      subtitle: description as string,
      image: null,
      icon: null,
      type,
      value: (id as string) || (value as string),
      isActive: selectedId === id || selectedId === value,
      imageMaxWidth: undefined,
      paymentPlanMetadata,
      eventId,
      membershipId,
      order: 0,
    };

    if (
      (type === IntegrationType.PaymentWindcave &&
        paymentMethodType !== "Account2Account") ||
      type === IntegrationType.PaymentStripe
    ) {
      if (item.bnplEnabled) {
        option.icon = "afterpay";

        const laybuyPrice = calculateBNPLPrice(
          totalForLaybuy,
          AFTERPAY_CONFIG,
          {
            showHiddenFees,
            fee: 0,
            type: item.transactionFeeType,
          }
        );

        option.subtitle = `4 payments from ${formatPrice(laybuyPrice, {
          currency: organization.currency,
        })}, every two weeks.`;
      } else if (!paymentPlanMetadata) {
        option.image = "/static/visa-mastercard.png";
      }

      // HACK: Temporary for Fiji
      if (organization.id === "353756ea-9104-4973-8b18-51f7d5f76b47") {
        option.image = "/static/visa-mastercard-amex.png";
        option.imageMaxWidth = "120px";
      }
      // HACK: Temporary for Hororata, only Windcave payments
      if (
        organization.id === "9c1be8aa-07d1-4480-be58-1845543964b3" &&
        type === IntegrationType.PaymentWindcave
      ) {
        option.image = "/static/farmlands-visa-mastercard.png";
        option.imageMaxWidth = "120px";
      }

      // HACK: Temporary for Chasing the Fox, AMEX VIP Lounge event, only Stripe payments.
      // Remove 2026-01-01
      if (
        eventId === "5d26d643-5b8c-49e5-b4c1-8c5fa8ac30f6" &&
        type === IntegrationType.PaymentStripe &&
        !item.bnplEnabled
      ) {
        option.image = "/static/amex.png";
        option.imageMaxWidth = "40px";
      }
    }

    if (
      type === IntegrationType.PaymentWindcave &&
      paymentMethodType === "Account2Account"
    ) {
      option.image = "/static/account2account.png";
    }

    if (type === IntegrationType.PaymentMpaisa) {
      option.image = "/static/mpaisa-pay.png";
    }

    if (type === IntegrationType.PaymentPin) {
      if (organization.address?.country === "AU") {
        option.image = "/static/visa-mastercard-amex.png";
      } else {
        option.image = "/static/visa-mastercard.png";
      }
    }

    if (isBNPLIntegrationType(type as IntegrationType)) {
      const config = BNPL_CONFIG[type as BNPLIntegrationType];
      option.icon = config.icon;

      const bnplPrice = calculateBNPLPrice(totalForLaybuy, config, {
        showHiddenFees,
        fee: 0,
        type: item.transactionFeeType,
      });

      option.subtitle = `${config.terms} equal payments from ${formatPrice(
        bnplPrice,
        {
          currency: organization.currency,
        }
      )} per ${config.period}.`;
    }

    // Hack: SYNTHONY
    // Remove 2026-04-01
    if (
      eventId === "f1ce89b4-a0cf-4f0b-b889-f16f6563919d" &&
      type === IntegrationType.PaymentStripe &&
      !value?.includes("Afterpay")
    ) {
      option.title = "American Express";
      option.image = "/static/amex.png";
      option.imageMaxWidth = "40px";
    }

    return option;
  }

  return {
    paymentType,
    payments: paymentOptionsVM,
    events: {
      handleItemClick,
    },
  };
};

const PaymentList = (props: PaymentListProps) => {
  const { hasFeature } = useOrganization();

  const {
    paymentType,
    payments,
    events: { handleItemClick },
  } = usePaymentListViewModel({
    ...props,
    pinPaymentEnabled: hasFeature("PinPaymentGateway"),
  });

  const { eventId, membershipId } = props;

  return (
    <Box>
      {payments.map((item) => {
        //! Temp hack: Afterpay won't like seeing the changing price in the subtitle (as total is based off selected payment option).
        // Solution for now is to hide the subtitle for Afterpay unless it's selected.
        const hideContent =
          (item.type === IntegrationType.PaymentAfterpay ||
            item.title === "Afterpay") &&
          !item.isActive;
        const InfoModal = (() => {
          if (isBNPLIntegrationType(item.type as BNPLIntegrationType)) {
            return withVariantCheckoutBNPLModal(
              item.type as BNPLIntegrationType
            )();
          }
          if (item.type === CustomPaymentType.OnHold) {
            return HoldOrderModal;
          }
          return null;
        })();
        return (
          <PaymentItem
            key={item.value}
            type={paymentType}
            value={item.value}
            isActive={item.isActive}
            onChange={handleItemClick}
            title={item.title}
            image={item.image}
            icon={item.icon}
            subtitle={!hideContent ? item.subtitle : undefined}
            InfoModal={
              !item.paymentPlanMetadata && (hideContent || !InfoModal)
                ? undefined
                : (props) => {
                    if (item.paymentPlanMetadata) {
                      return (
                        <PaymentPlansModal
                          {...props}
                          metadata={item.paymentPlanMetadata}
                          eventId={eventId}
                          membershipId={membershipId}
                        />
                      );
                    }
                    return <InfoModal {...props} />;
                  }
            }
            imageMaxWidth={item.imageMaxWidth}
          />
        );
      })}
    </Box>
  );
};

export default PaymentList;
